import React, { useEffect, useMemo, useState } from 'react';
import { ArticleSummaryLoadError, fetchArticleDetail, fetchArticleSummaries } from '../services/api';
import { ArticleDetail as ArticleDetailType, ArticleSummary } from '../types/article';
import { formatDateYmd, toEpochMs } from '../utils/time';
import { toWebpPath } from '../utils/image';
import { useContent } from '../contexts/ContentContext';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { publicIso } from '../services/publicRecords';
import BackToListButton from './BackToListButton';
import ContentPageHeader from './PageHeader';
import { articleTitle, articleLead, articleBody } from '../utils/articlePresentation';
import { ArticleBlock, ArticleBlockImage, readArticleDocument, sanitizeArticleHtml, articleOutline } from '../utils/articleDocument';

interface ArticleDetailProps {
    year: string;
    month: string;
    id: string;
}


const escapeHtml = (value: string): string => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const isTableSeparator = (line: string): boolean => {
    return /^\s*\|?\s*:?[-]+:?\s*(\|\s*:?[-]+:?\s*)+\|?\s*$/.test(line);
};

const splitTableRow = (line: string, columnCount: number | null = null): string[] => {
    let trimmed = line.trim();
    if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
    if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
    let cells = trimmed.split('|').map(cell => cell.trim());
    if (columnCount !== null) {
        if (cells.length < columnCount) {
            cells = cells.concat(Array(columnCount - cells.length).fill(''));
        } else if (cells.length > columnCount) {
            cells = cells.slice(0, columnCount);
        }
    }
    return cells;
};

const parseTableMarkdown = (markdown?: string): { headers: string[]; rows: string[][] } | null => {
    if (!markdown) return null;
    const lines = markdown
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);
    if (lines.length < 2 || !isTableSeparator(lines[1])) return null;
    const headers = splitTableRow(lines[0]);
    if (!headers.length) return null;
    const rows: string[][] = [];
    for (let i = 2; i < lines.length; i += 1) {
        const rowLine = lines[i];
        if (!rowLine.includes('|')) break;
        if (isTableSeparator(rowLine)) continue;
        rows.push(splitTableRow(rowLine, headers.length));
    }
    return { headers, rows };
};

const tableMarkdownToHtml = (markdown?: string): string => {
    const parsed = markdown ? parseTableMarkdown(markdown) : null;
    if (!parsed) return '';
    const headerHtml = parsed.headers
        .map(cell => `<th>${escapeHtml(cell)}</th>`)
        .join('');
    const bodyHtml = parsed.rows
        .map(row => {
            const cells = row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('');
            return `<tr>${cells}</tr>`;
        })
        .join('');
    return `
        <table class="article-table">
            <thead><tr>${headerHtml}</tr></thead>
            <tbody>${bodyHtml}</tbody>
        </table>
    `.trim();
};

const normalizeArticleImageSrc = (src?: string, roomImageId?: string | null): string => {
    if (src && src.startsWith('yakkyoku-oj3.tech/htdocs/public')) {
        const trimmed = src.slice('yakkyoku-oj3.tech/htdocs/public'.length);
        const normalized = trimmed.startsWith('/')
            ? `./${trimmed.slice(1)}`
            : trimmed.startsWith('articles/')
                ? `./${trimmed}`
                : trimmed;
        return toWebpPath(normalized || '');
    }
    if (src && src.startsWith('/articles/')) {
        return toWebpPath(`.${src}`);
    }
    if (!src && roomImageId) {
        return `./assets/room_images/${roomImageId}.webp`;
    }
    return toWebpPath(src || '');
};

const buildImageStyle = (image: ArticleBlockImage): string => {
    const styles: string[] = [];
    const width = image.displayWidth ? parseInt(image.displayWidth, 10) : 0;
    const height = image.displayHeight ? parseInt(image.displayHeight, 10) : 0;
    if (Number.isFinite(width) && width > 0) styles.push(`width="${width}"`);
    if (Number.isFinite(height) && height > 0) styles.push(`height="${height}"`);
    return styles.length ? ` ${styles.join(' ')}` : '';
};

const blocksToHtml = (blocks: ArticleBlock[], unsupported: (type: string) => string): string => {
    if (!blocks.length) return '';
    const htmlParts: string[] = [];
    blocks.forEach(block => {
        if (block.type === 'heading') {
            const level = block.level || 1;
            htmlParts.push(`<h${level} id="article-block-${block.id}">${block.content || ''}</h${level}>`);
        } else if (block.type === 'paragraph') {
            htmlParts.push(`<p>${block.content || ''}</p>`);
        } else if (block.type === 'quote') {
            htmlParts.push(`<blockquote>${block.content || ''}</blockquote>`);
        } else if (block.type === 'list') {
            const tag = block.style === 'ordered' ? 'ol' : 'ul';
            const items = (block.items || []).map(item => `<li>${item}</li>`).join('');
            htmlParts.push(`<${tag}>${items}</${tag}>`);
        } else if (block.type === 'code') {
            htmlParts.push(`<pre><code>${escapeHtml(block.content || '')}</code></pre>`);
        } else if (block.type === 'table') {
            const tableHtml = tableMarkdownToHtml(block.content);
            if (tableHtml) htmlParts.push(tableHtml);
        } else if (block.type === 'image') {
            const images = block.images || [];
            const layout = block.layout || 'grid';
            const layoutClass = layout === 'masonry'
                ? 'article-image-list article-image-list-masonry'
                : layout === 'carousel'
                    ? 'article-image-list article-image-list-carousel'
                    : 'article-image-list';
            const imageHtml = images.map(image => {
                const src = normalizeArticleImageSrc(image.src, image.roomImageId);
                const alt = image.alt || '';
                const styleAttr = buildImageStyle(image);
                const imgTag = `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"${styleAttr}>`;
                const media = image.link
                    ? `<a href="${escapeHtml(image.link)}" target="_blank" rel="noopener">${imgTag}</a>`
                    : imgTag;
                const caption = image.caption ? `<p class="article-image-caption">${image.caption}</p>` : '';
                return `<div class="article-image-item">${media}${caption}</div>`;
            }).join('');
            htmlParts.push(`<div class="${layoutClass}">${imageHtml}</div>`);
        } else if (block.type === 'divider') {
            htmlParts.push('<hr>');
        } else if (block.type === 'link') {
            const url = block.url || '';
            const title = block.title || block.content || url;
            if (url) {
                htmlParts.push(`
                    <div class="article-link-block">
                        <a href="${escapeHtml(url)}" target="_blank" rel="noopener">
                            <span class="article-link-title">${title}</span>
                            <span class="article-link-url">${url}</span>
                        </a>
                    </div>
                `.trim());
            } else if (title) {
                htmlParts.push(`<p>${title}</p>`);
            }
        } else if (block.type === 'embed') {
            if (block.url) htmlParts.push(`<iframe src="${escapeHtml(block.url)}"></iframe>`);
        } else if (block.type === 'unsupported') {
            htmlParts.push(`<div data-article-unsupported="${escapeHtml(block.originalType)}"><p>${escapeHtml(unsupported(block.originalType))}</p><pre>${escapeHtml(JSON.stringify(block.raw))}</pre></div>`);
        }
    });
    return sanitizeArticleHtml(htmlParts.join('\n'));
};

const ArticleDetail: React.FC<ArticleDetailProps> = ({ year, month, id }) => {
    const { t } = useContent();
    const [article, setArticle] = useState<ArticleDetailType | null>(null);
    const [recentArticles, setRecentArticles] = useState<ArticleSummary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [recentError, setRecentError] = useState(false);
    const [recentAttempt, setRecentAttempt] = useState(0);
    const blockHtml = useMemo(() => {
        if (!article?.content) return '';
        try {
            return blocksToHtml(readArticleDocument(article.content).blocks, type => t('articlePage.unsupported', { type }));
        } catch {
            return `<p>${escapeHtml(t('articlePage.unsupported', { type: 'document' }))}</p><pre>${escapeHtml(article.content)}</pre>`;
        }
    }, [article?.content, t]);

    useEffect(() => {
        let active = true;
        setIsLoading(true); setError(null); setArticle(null);
        fetchArticleDetail(year, month, id).then((detail: ArticleDetailType) => {
            const published = publicIso(detail.published_at);
            if (detail.status && !['public', 'published'].includes(detail.status)) throw new Error('unpublished');
            if (published && Date.parse(published) > Date.now()) throw new Error('future');
            if (active) setArticle(detail);
        }).catch(() => { if (active) setError('error'); }).finally(() => { if (active) setIsLoading(false); });
        return () => { active = false; };
    }, [year, month, id, attempt]);

    useEffect(() => {
        let active = true;
        setRecentError(false);
        const update = (summaries: ArticleSummary[]) => { if (active) setRecentArticles(summaries.filter(item => item.id !== id).slice(0, 6)); };
        fetchArticleSummaries(7).then(update).catch(error => {
            if (active) { update(error instanceof ArticleSummaryLoadError ? error.articles : []); setRecentError(true); }
        });
        return () => { active = false; };
    }, [year, month, id, recentAttempt]);

    const title = articleTitle(article?.title, t('articlePage.untitled'));
    const outline = useMemo(() => articleBody(articleOutline(article?.content ? blockHtml : sanitizeArticleHtml(article?.content_html || '')), title), [article, blockHtml, title]);
    const focusHeading = (targetId: string) => {
        const heading = document.getElementById(targetId);
        if (!heading) return;
        heading.tabIndex = -1;
        heading.scrollIntoView({ block: 'start' });
        heading.focus({ preventScroll: true });
    };
    useEffect(() => {
        if (isLoading) return;
        const jump = () => { try { focusHeading(decodeURIComponent(window.location.hash.slice(1))); } catch {} };
        const frame = requestAnimationFrame(jump);
        window.addEventListener('hashchange', jump);
        return () => { cancelAnimationFrame(frame); window.removeEventListener('hashchange', jump); };
    }, [isLoading, outline]);
    const publishedMs = toEpochMs(article?.published_at ?? article?.created_at);
    const publishedLabel = publishedMs ? formatDateYmd(publishedMs) : '';

    usePageMetadata({
        title: article ? `${title} / ${t('notesArticleDetail.text3')}` : (t('notesArticleDetail.text4')),
        description: article?.excerpt || undefined,
        image: article?.cover_image,
        type: 'article'
    });

    const heading = <ContentPageHeader sidebar title={!isLoading && article ? title : undefined} actions={<BackToListButton href="/?page=article" label={t('notes.back')} />} />;
    if (isLoading) {
        return <>{heading}<p role="status" className="dark:text-gray-300">{t('notesArticleDetail.text5')}</p></>;
    }

    if (error) {
        return <>{heading}<div role="alert"><p>{t('notes.error')}</p><button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div></>;
    }

    if (!article) {
        return <>{heading}<p className="text-red-500">{t('notesArticleDetail.text6')}</p></>;
    }

    const contentHtml = outline.html;

    return (
        <div className="lab-page lab-article lab-content-page">
            {heading}

            <div className="lg:flex lg:gap-6">
                <article className="flex-1 min-w-0 bg-white dark:bg-gray-800 ivory:bg-coffee-100 border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 rounded-2xl shadow-md overflow-hidden">
                    {article.cover_image && (
                        <div
                            className="article-cover bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200"
                        >
                            <img
                                src={toWebpPath(article.cover_image)}
                                alt={title}
                                className="article-cover-image"
                            />
                        </div>
                    )}

                    <div className="p-6 md:p-8">
                        <div className="flex flex-col gap-2 mb-6">
                            <p className="text-xs uppercase tracking-[0.2em] text-gray-400 dark:text-gray-500 ivory:text-coffee-400">
                                {publishedLabel}
                            </p>
                            {articleLead(title, article.excerpt, contentHtml) && (
                                <p className="text-sm text-gray-600 dark:text-gray-300 ivory:text-coffee-600 whitespace-pre-line">
                                    {articleLead(title, article.excerpt, contentHtml)}
                                </p>
                            )}
                        </div>

                        {outline.headings.length > 0 && <details className="lab-toc mb-6 border-y py-4"><summary className="cursor-pointer font-bold">{t('notes.toc')}</summary><nav aria-label={t('notes.toc')}><ol className="mt-4 space-y-3">{outline.headings.map(heading => <li key={heading.id}><a href={'#' + heading.id} onClick={event => { event.preventDefault(); window.history.pushState({}, '', '#' + heading.id); focusHeading(heading.id); }}>{heading.title}</a></li>)}</ol></nav></details>}
                        {contentHtml ? (
                            <div
                                className="article-content"
                                dangerouslySetInnerHTML={{ __html: contentHtml }}
                            />
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400">
                                {t('notesArticleDetail.text8')}
                            </p>
                        )}
                    </div>
                </article>

                <aside className="mt-8 lg:mt-0 lg:w-72">
                    <div className="sticky top-24 space-y-4">
                        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-800 ivory:bg-coffee-100 shadow-sm p-4">
                            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 ivory:text-coffee-700 uppercase tracking-widest mb-4">
                                {t('notesArticleDetail.text9')}
                            </h2>
                            <div className="space-y-3">
                                {recentError && <div role="alert"><p>{t('notes.recentError')}</p><button className="lab-button" onClick={() => setRecentAttempt(value => value + 1)}>{t('common.retry')}</button></div>}
                                {!recentError && recentArticles.length === 0 && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t('notesArticleDetail.text10')}
                                    </p>
                                )}
                                {recentArticles.map(item => {
                                    const itemTitle = articleTitle(item.title, t('articlePage.untitled'));
                                    const itemDate = formatDateYmd(item.publishedAt) || '';
                                    const publishedAtParam = item.publishedAt ? encodeURIComponent(String(item.publishedAt)) : '';
                                    return (
                                        <a
                                            key={`${item.year}-${item.month}-${item.id}`}
                                            href={`/?page=article&view=detail&year=${item.year}&month=${item.month}&id=${item.id}${publishedAtParam ? `&published_at=${publishedAtParam}` : ''}`}
                                            className="flex gap-3 items-start group"
                                        >
                                            <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200 flex-shrink-0">
                                                {item.coverImage ? (
                                                    <img src={toWebpPath(item.coverImage)} alt={itemTitle} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[10px] uppercase text-gray-400 dark:text-gray-500 ivory:text-coffee-400">
                                                        {t('articlePage.noCover')}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs text-gray-400 dark:text-gray-500 ivory:text-coffee-400">
                                                    {itemDate}
                                                </p>
                                                <p className="article-title text-sm font-semibold text-gray-800 dark:text-gray-200 ivory:text-coffee-700 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                                    {itemTitle}
                                                </p>
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default ArticleDetail;
